import { DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class PromptGeneratorService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly defaultPrefix = 'general';
  private readonly translate = inject(TranslateService);
  private readonly currentRoute = signal<string>(this.router.url);
  private readonly knownRoutes: PromptSettings[] = [
    { urlId: ['mercer-support'], prompts: 5, prefix: 'position-evaluation' },
    {
      urlId: ['merit-support', 'budget-allocation'],
      prompts: 3,
      prefix: 'merit'
    }
  ];
  private readonly defaultSettings: PromptSettings = {
    urlId: [],
    prompts: 5,
    prefix: this.defaultPrefix
  };
  private readonly knownSettings = signal<PromptSettings>(this.defaultSettings, {
    equal: (a, b) => this.isEqualSettings(a, b)
  });

  readonly prompts = signal<string[]>([]);

  constructor() {
    this.setSettings();
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: NavigationEnd) => {
        this.currentRoute.set(event.url);
        this.setSettings();
      });

    effect(() => {
      const settings = this.knownSettings();
      this.prompts.set(
        Array.from({ length: settings.prompts }, (_, i) =>
          this.translate.instant(`chat-bot.prompts.${settings.prefix}-prompt-${i + 1}`)
        )
      );
    });
  }

  private setSettings(): void {
    const settings = this.findPromptSettings();
    this.knownSettings.set(settings);
  }

  private findPromptSettings(): PromptSettings | undefined {
    const currentUrl = this.currentRoute();
    const settings = this.knownRoutes.find(route => {
      return route.urlId.some(id => {
        if (currentUrl.includes(id)) {
          return true;
        }
        return false;
      });
    });

    return settings || this.defaultSettings;
  }

  private isEqualSettings(a: PromptSettings, b: PromptSettings): boolean {
    return a.urlId.join(',') === b.urlId.join(',') && a.prompts === b.prompts && a.prefix === b.prefix;
  }
}

type PromptSettings = {
  urlId: string[];
  prompts: number;
  prefix: string;
};
